import {Snippet} from '../../../../Models/Snippet';
import {formatDuration} from '../../../Support/Formatting/formatDuration';
import {formatDistance} from '../../../Support/Formatting/formatDistance';
import {formatAveragePower} from '../../../Support/Formatting/formatPower';
import {formatAverageSplit} from '../../../Support/Formatting/formatSplit';
import {formatAveragePeakForce} from '../../../Support/Formatting/formatPeakForce';
import {Speed} from '../../../../Domain/Speed';
import {formatAverageDriveTime} from '../../../Support/Formatting/formatDriveTime';
import {formatAverageRecoverTime} from '../../../Support/Formatting/formatRecoverTime';
import {formatAverageEnergy} from '../../../Support/Formatting/formatEnergy';
import {formatAverageStrokeLength} from '../../../Support/Formatting/formatStrokeLength';
import {format} from 'date-fns';
import {Duration} from '../../../../Domain/Duration';
import {Distance} from '../../../../Domain/Distance';
import {Power} from '../../../../Domain/Power';
import {Force} from '../../../../Domain/Force';
import {Energy} from '../../../../Domain/Energy';
import {formatAveragePeakForcePosition} from '../../../Support/Formatting/formatPeakForcePosition';
import {formatAverageRelativePeakForcePosition} from '../../../Support/Formatting/formatRelativePeakForcePosition';
import {formatAverageRSquared, formatRSquared} from '../../../Support/Formatting/formatRSquared';
import {Fraction} from '../../../../Domain/Fraction';

export enum ReportField {
    ElapsedTime = 'elapsed-time',
    TraveledDistance = 'traveled-distance',
    AveragePower = 'average-power',
    AverageSplit = 'average-split',
    AveragePeakForce = 'average-peak-force',
    AverageDriveTime = 'average-drive-time',
    AverageRecoverTime = 'average-recover-time',
    AverageEnergyPerStroke = 'average-energy-per-stroke',
    AverageStrokeLength = 'average-stroke-length',
    AverageRelativePeakForcePosition = 'average-relative-peak-force-position',
    AverageRSquaredTotalStroke = 'average-r-squared-total-stroke',
    AverageRSquaredPrePeak = 'average-r-squared-pre-peak',
    AverageRSquaredPostPeak = 'average-r-squared-post-peak',
    ExecutedAt = 'executed-at',
}

export function reportFieldToLabel(field: ReportField): string {
    switch (field) {
        case ReportField.ElapsedTime:
            return 'Elapsed Time';
        case ReportField.TraveledDistance:
            return 'Traveled Distance (m)';
        case ReportField.AveragePower:
            return 'Ø Power (W)';
        case ReportField.AverageSplit:
            return 'Ø Split (/500m)';
        case ReportField.AveragePeakForce:
            return 'Ø Peak Force (N)';
        case ReportField.AverageDriveTime:
            return 'Ø Drive Time (s)';
        case ReportField.AverageRecoverTime:
            return 'Ø Recover Time (s)';
        case ReportField.AverageEnergyPerStroke:
            return 'Ø Energy per Stroke (J)';
        case ReportField.AverageStrokeLength:
            return 'Ø Stroke length (m)';
        case ReportField.AverageRelativePeakForcePosition:
            return 'Ø Rel. Peak Pos. (%)';
        case ReportField.AverageRSquaredTotalStroke:
            return 'Ø R² Total';
        case ReportField.AverageRSquaredPrePeak:
            return 'Ø R² Pre-Peak';
        case ReportField.AverageRSquaredPostPeak:
            return 'Ø R² Post-Peak';
        case ReportField.ExecutedAt:
            return 'Date';
    }
}

export function reportFieldToFormattedValue(field: ReportField, snippet: Snippet): string {
    switch (field) {
        case ReportField.ElapsedTime:
            return formatDuration(snippet.elapsedTime).formatted;
        case ReportField.TraveledDistance:
            return formatDistance(snippet.traveledDistance).formatted;
        case ReportField.AveragePower:
            return formatAveragePower(snippet.averagePower).formatted;
        case ReportField.AverageSplit:
            return formatAverageSplit(Speed.fromDurationPer500Meters(snippet.averageSplit)).formatted;
        case ReportField.AveragePeakForce:
            return formatAveragePeakForce(snippet.averagePeakForce).formatted;
        case ReportField.AverageDriveTime:
            return formatAverageDriveTime(snippet.averageDriveTime).formatted;
        case ReportField.AverageRecoverTime:
            return formatAverageRecoverTime(snippet.averageRecoverTime).formatted;
        case ReportField.AverageEnergyPerStroke:
            return formatAverageEnergy(snippet.averageEnergyPerStroke).formatted;
        case ReportField.AverageStrokeLength:
            return formatAverageStrokeLength(snippet.averageStrokeLength).formatted;
        case ReportField.AverageRelativePeakForcePosition:
            return formatAverageRelativePeakForcePosition(snippet.averageRelativePeakForcePosition).formatted;
        case ReportField.AverageRSquaredTotalStroke:
            return snippet.rSquaredTotalStroke ? formatRSquared(snippet.rSquaredTotalStroke.value).formatted : '-';
        case ReportField.AverageRSquaredPrePeak:
            return snippet.rSquaredPrePeak ? formatRSquared(snippet.rSquaredPrePeak.value).formatted : '-';
        case ReportField.AverageRSquaredPostPeak:
            return snippet.rSquaredPostPeak ? formatRSquared(snippet.rSquaredPostPeak.value).formatted : '-';
        case ReportField.ExecutedAt:
            return format(snippet.publicWorkout.executedAt, 'dd-MM-yyyy');
    }
}

export function graphValueForField(field: ReportField, snippet: Snippet): number | null {
    switch (field) {
        case ReportField.ElapsedTime:
            return snippet.elapsedTime.milliseconds;
        case ReportField.TraveledDistance:
            return snippet.traveledDistance.millimeters;
        case ReportField.AveragePower:
            return snippet.averagePower?.watts ?? null;
        case ReportField.AverageSplit:
            return snippet.averageSplit?.milliseconds ?? null;
        case ReportField.AveragePeakForce:
            return snippet.averagePeakForce?.newton ?? null;
        case ReportField.AverageDriveTime:
            return snippet.averageDriveTime?.milliseconds ?? null;
        case ReportField.AverageRecoverTime:
            return snippet.averageRecoverTime?.milliseconds ?? null;
        case ReportField.AverageEnergyPerStroke:
            return snippet.averageEnergyPerStroke?.joules ?? null;
        case ReportField.AverageStrokeLength:
            return snippet.averageStrokeLength?.millimeters ?? null;
        case ReportField.AverageRelativePeakForcePosition:
            return snippet.averageRelativePeakForcePosition?.percentage ?? null;
        case ReportField.AverageRSquaredTotalStroke:
            return snippet.rSquaredTotalStroke?.percentage ?? null;
        case ReportField.AverageRSquaredPrePeak:
            return snippet.rSquaredPrePeak?.percentage ?? null;
        case ReportField.AverageRSquaredPostPeak:
            return snippet.rSquaredPostPeak?.percentage ?? null;
        case ReportField.ExecutedAt:
            return snippet.publicWorkout.executedAt.getTime();
        case undefined:
            return null;
    }
}

export function graphValueToFormattedValue(field: ReportField, graphValue: number): string {
    switch (field) {
        case ReportField.ElapsedTime:
            return formatDuration(Duration.fromMilliseconds(graphValue)).formatted;
        case ReportField.TraveledDistance:
            return formatDistance(Distance.fromMillimeters(graphValue)).formatted;
        case ReportField.AveragePower:
            return formatAveragePower(Power.fromWatts(graphValue)).formatted;
        case ReportField.AverageSplit:
            return formatAverageSplit(Speed.fromDurationPer500Meters(Duration.fromMilliseconds(graphValue))).formatted;
        case ReportField.AveragePeakForce:
            return formatAveragePeakForce(Force.fromNewton(graphValue)).formatted;
        case ReportField.AverageDriveTime:
            return formatAverageDriveTime(Duration.fromMilliseconds(graphValue)).formatted;
        case ReportField.AverageRecoverTime:
            return formatAverageRecoverTime(Duration.fromMilliseconds(graphValue)).formatted;
        case ReportField.AverageEnergyPerStroke:
            return formatAverageEnergy(Energy.fromJoules(graphValue)).formatted;
        case ReportField.AverageStrokeLength:
            return formatAverageStrokeLength(Distance.fromMillimeters(graphValue)).formatted;
        case ReportField.AverageRelativePeakForcePosition:
            return formatAverageRelativePeakForcePosition(Fraction.fromValue(graphValue)).formatted;
        case ReportField.AverageRSquaredTotalStroke:
            return formatAverageRSquared(graphValue).formatted;
        case ReportField.AverageRSquaredPrePeak:
            return formatAverageRSquared(graphValue).formatted;
        case ReportField.AverageRSquaredPostPeak:
            return formatAverageRSquared(graphValue).formatted;
        case ReportField.ExecutedAt:
            return format(new Date(graphValue), 'dd-MM-yyyy');
        case undefined:
            return ' ';
    }
}
